import type { Currency } from "@/lib/format"
import type { Location } from "@/types/content"

/** Used only when the daily BrsApi cache is missing. */
export const FALLBACK_USD_TO_TOMAN = 240_000

/**
 * AI API only. Market USD stays unchanged for servers.
 * 3.5% above the free-market toman rate, then rounded to a whole toman.
 */
export const AI_USD_MARKUP = 1.035

export function aiTomanPerUsd(marketTomanPerUsd: number) {
  if (!Number.isFinite(marketTomanPerUsd) || marketTomanPerUsd <= 0) return 0
  return Math.round(marketTomanPerUsd * AI_USD_MARKUP)
}

export type BrsGoldItem = {
  symbol?: unknown
  name?: unknown
  name_en?: unknown
  price?: unknown
  unit?: unknown
}

export type UsdRateSnapshot = {
  tomanPerUsd: number
  source: "brsapi" | "fallback"
  fetchedAt: string | null
  tehranDate: string | null
  symbol: string | null
  name: string | null
}

export function isForeignLocation(location?: Pick<Location, "countryCode"> | null) {
  const code = location?.countryCode?.trim().toUpperCase()
  return Boolean(code && code !== "IR")
}

/** Convert USD to تومان. Iran-only prices must not go through this. */
export function usdToToman(usd: number, tomanPerUsd: number) {
  if (!Number.isFinite(usd) || !Number.isFinite(tomanPerUsd) || tomanPerUsd <= 0) return 0
  return Math.round(usd * tomanPerUsd)
}

export function shouldConvertUsdToToman(
  currency: Currency | string | null | undefined,
  location?: Pick<Location, "countryCode"> | null,
) {
  if (currency !== "USD") return false
  // Iran-tagged USD stays IRT. Untagged USD (multi-region VPS) follows the market rate.
  if (location?.countryCode) return isForeignLocation(location)
  return true
}

export function localizeForeignUsdPlan<T extends {
  price: number
  currency: Currency
  location?: Pick<Location, "countryCode"> | null
  setupFee?: number | null
  priceUsd?: number | null
}>(plan: T, tomanPerUsd: number): T {
  if (!shouldConvertUsdToToman(plan.currency, plan.location)) return plan
  return {
    ...plan,
    priceUsd: plan.price,
    price: usdToToman(plan.price, tomanPerUsd),
    setupFee: plan.setupFee == null ? plan.setupFee : usdToToman(plan.setupFee, tomanPerUsd),
    currency: "IRT",
  }
}

function asText(value: unknown) {
  return typeof value === "string" ? value.trim() : ""
}

function asNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.replace(/,/g, ""))
    if (Number.isFinite(n)) return n
  }
  return null
}

/** Free Gold_Currency unit is تومان; tolerate ریال just in case. */
export function brsPriceToToman(price: unknown, unit: unknown) {
  const n = asNumber(price)
  if (n == null) return null
  const u = asText(unit)
  if (u.includes("ریال") || u.toLowerCase() === "rial" || u.toLowerCase() === "irr") return n / 10
  return n
}

/**
 * Free endpoint: https://Api.BrsApi.ir/Market/Gold_Currency.php?key=
 * Pick بازار آزاد دلار (symbol USD / name دلار), never tether or AUD/CAD.
 */
export function pickUsdTomanFromBrsPayload(payload: unknown): {
  tomanPerUsd: number
  symbol: string
  name: string
} | null {
  const currency = extractCurrencyList(payload)
  const usd = currency.find((item) => asText(item.symbol).toUpperCase() === "USD")
    ?? currency.find((item) => asText(item.name) === "دلار")
  if (!usd) return null
  const toman = brsPriceToToman(usd.price, usd.unit)
  if (toman == null || toman <= 0) return null
  return {
    tomanPerUsd: toman,
    symbol: asText(usd.symbol) || "USD",
    name: asText(usd.name) || "دلار",
  }
}

function extractCurrencyList(payload: unknown): BrsGoldItem[] {
  if (Array.isArray(payload)) return payload.filter(isItem)
  if (!payload || typeof payload !== "object") return []
  const record = payload as Record<string, unknown>
  const currency = record.currency
  if (Array.isArray(currency)) return currency.filter(isItem)
  return []
}

function isItem(value: unknown): value is BrsGoldItem {
  return Boolean(value) && typeof value === "object"
}
